import React from "react";
import { ImageReorderModal } from "../ImageReorderModal";

/**
 * Generic drop zone component to pair with useUploadOrPickImages drag & drop API.
 * It is intentionally style-light so parent can customize.
 */
export interface ImageDropZoneProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** hook instance returned by useUploadOrPickImages */
  uploader: {
    getDropZoneProps?: (
      overrides?: React.HTMLAttributes<HTMLElement>
    ) => React.HTMLAttributes<HTMLElement>;
    isDragging?: boolean;
    /** Optional reorder-modal state — returned by useUploadOrPickImages when dragAndDrop.enabled */
    showReorderModal?: boolean;
    pendingDropFiles?: File[];
    handleReorderConfirm?: (orderedFiles: File[]) => void;
    handleReorderCancel?: () => void;
  };
  /** Optional: show a default inner label when no children provided */
  label?: string;
  /** Optional: custom class applied while dragging */
  draggingClassName?: string;
  /** Show a built-in overlay while dragging */
  showOverlay?: boolean;
  /** Custom overlay content (defaults to dynamic message) */
  overlayContent?: React.ReactNode;
  /** Inline styles for overlay layer */
  overlayStyles?: React.CSSProperties;
}

export const ImageDropZone: React.FC<ImageDropZoneProps> = ({
  uploader,
  children,
  label = "Drag & drop images here or click to browse",
  draggingClassName = "dragging",
  className = "",
  showOverlay = true,
  overlayContent,
  overlayStyles,
  ...rest
}) => {
  const {
    getDropZoneProps,
    isDragging,
    showReorderModal,
    pendingDropFiles,
    handleReorderConfirm,
    handleReorderCancel,
  } = uploader || {};
  const zoneProps = getDropZoneProps ? getDropZoneProps() : {};
  const finalOverlayContent =
    overlayContent || (isDragging ? "Release to upload" : label);
  const needsRelative = !(rest.style && (rest.style as any).position);

  const hasReorderModal =
    showReorderModal !== undefined &&
    pendingDropFiles !== undefined &&
    handleReorderConfirm !== undefined &&
    handleReorderCancel !== undefined;

  return (
    <>
    <div
      {...zoneProps}
      {...rest}
      className={`${className} ${isDragging ? draggingClassName : ""}`.trim()}
      style={{
        position: needsRelative ? "relative" : undefined,
        ...rest.style,
      }}
      role="button"
      tabIndex={0}
      aria-label={label}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          // trigger hidden input if present inside
          const input = (e.currentTarget as HTMLElement).querySelector(
            'input[type="file"]'
          ) as HTMLInputElement | null;
          input?.click();
        }
      }}
    >
      {children || <span>{finalOverlayContent}</span>}
      {showOverlay && isDragging && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255,129,58,0.15)",
            color: "#ff813a",
            fontWeight: 600,
            fontSize: "1rem",
            pointerEvents: "none",
            zIndex: 2,
            outline: "2px dashed #ff813a",
            ...overlayStyles,
          }}
          data-testid="drag-overlay"
        >
          {finalOverlayContent}
        </div>
      )}
    </div>
    {hasReorderModal && (
      <ImageReorderModal
        open={showReorderModal!}
        files={pendingDropFiles!}
        onConfirm={handleReorderConfirm!}
        onCancel={handleReorderCancel!}
      />
    )}
    </>
  );
};

export default ImageDropZone;
